/* Copyright (c) 2026 Xperi. All Rights Reserved. */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { AppError } from "@tivocorp/data-domain-lib";

import { ErrorTypes } from "../../model/error";
import { BaseErrorMapper } from "../../model/errorMapper/BaseErrorMapper";
import { Locale, LocaleKeys } from "../../model/locale";

/** Shape of a carousel as far as the emptiness check is concerned. */
interface CarouselWithFeedItems {
  feedItems?: { feedItems?: unknown[] };
}

/**
 * Catalog code for the Home Screen error dialog. Shown on its own when the
 * Smart Carousel screens call succeeds with nothing displayable, and with a
 * sub-error suffix (`A707-G500`) when a layer of the call failed.
 *
 * Taken from `ErrorTypes` rather than written out here, so the number has one
 * declaration. The key it comes from is named for `/bind`, which emits the same
 * number — the known A707 collision tracked in the error-code drift guard.
 */
export const HOMESCREEN_ERROR_CODE: string =
  ErrorTypes.BIND_CLIENT_BAD_REQUEST_A707;

/** Diagnostic message logged/reported for a 200 OK with empty predictions. */
const HOMESCREEN_EMPTY_RESPONSE_MESSAGE: string =
  "Home screen returned empty carousels";

/**
 * Localised title for the A707 dialog ("Internal Error").
 *
 * Read on each call rather than captured at module load: the locale files are
 * imported asynchronously by `Locale.setLocale()`, so a value resolved at
 * import time would be the empty string.
 *
 * Shared by the mapper (which stores it on the AppError) and the view (which
 * renders it), so the dialog and the reported error never drift apart.
 */
export const getHomeScreenErrorTitle = (): string =>
  Locale.getString(LocaleKeys.HOME_SCREEN_ERROR_TITLE);

/** Localised body for the A707 dialog. See {@link getHomeScreenErrorTitle}. */
export const getHomeScreenErrorDescription = (): string =>
  Locale.getString(LocaleKeys.HOME_SCREEN_ERROR_MESSAGE);

/**
 * Error mapper for the Home Screen (A707).
 *
 * A707 is triggered when the user enters the Home Screen and:
 * - The Smart Carousel API (/v1/smart-carousel/screens/{screenName}) returns
 *   an error, OR
 * - The API returns HTTP 200 but carousels are empty (empty predictions), OR
 * - A timeout occurs during the feed items request
 *
 * The sub-error code comes from {@link ErrorCodeUtil}, which is shared by every
 * screen: it resolves the layer that detected the failure, so the same failure
 * always reports the same sub-code no matter which screen saw it.
 * - N6xx: HTTP client / transport — no HTTP response received
 * - S6xx: service context — token, service location, response integrity
 * - G4xx-G6xx: HTTP status returned by the Smart Carousel API
 * - none: 200 OK with nothing displayable, or a failure no layer recognises
 *
 * A sub-code is never guessed. When no layer recognises the error it is left
 * empty and the screen shows bare `A707` — per the catalog's implementation
 * guidance, a near-enough code is worse than none because it looks precise.
 */
export class HomeScreenErrorMapper extends BaseErrorMapper {
  /**
   * Maps a raw error thrown during the Home Screen Smart Carousel API call to
   * an A707 AppError with the appropriate sub-error code.
   *
   * @param error - The raw error thrown by the HTTP client or service layer
   * @returns AppError with code "A707" and a sub-error suffix, or with no
   *          suffix when no layer recognised the failure
   *
   * @example
   * // ENOTFOUND              → A707-N601
   * // ECONNREFUSED           → A707-N602
   * // ECONNABORTED           → A707-N603
   * // RequiredError          → A707-N604
   * // ETIMEDOUT              → A707-S612
   * // "Not Authenticated"    → A707-S617
   * // SLS lookup failure     → A707-S613
   * // SyntaxError (demarshal)→ A707-S616
   * // HTTP 401               → A707-G401
   * // HTTP 500               → A707-G500
   * // HTTP 418 (unmapped)    → A707-G600
   * // Unrecognised error     → A707
   */
  public static fromError(error: any): AppError {
    return this.buildAppError(
      HOMESCREEN_ERROR_CODE,
      this.extractMessage(error),
      this.extractSubErrorCode(error),
      getHomeScreenErrorTitle(),
      getHomeScreenErrorDescription()
    );
  }

  /**
   * Creates an A707 AppError for a semantically empty Home Screen response.
   *
   * Called when the Smart Carousel API returns HTTP 200 OK but:
   * - The `carousels` array is empty ([]), OR
   * - All carousels have empty `feedItems.feedItems` arrays
   *
   * No sub-error code is appended — the failure is not at the network,
   * service context, or HTTP layer. The API succeeded but returned no
   * displayable content ("empty predictions").
   *
   * Displayed to the user as plain "A707" (no suffix).
   *
   * @returns AppError with code "A707" and no sub-error code
   */
  public static fromEmptyResponse(): AppError {
    return this.buildAppError(
      HOMESCREEN_ERROR_CODE,
      HOMESCREEN_EMPTY_RESPONSE_MESSAGE,
      undefined,
      getHomeScreenErrorTitle(),
      getHomeScreenErrorDescription()
    );
  }

  /**
   * Whether an error is the one {@link fromEmptyResponse} produces — a call
   * that succeeded at every layer and simply had nothing to show.
   *
   * Callers use it to treat "empty predictions" differently from a failure:
   * there is nothing to recover from by calling again immediately, so it is
   * not worth retrying in the background.
   *
   * @param error - The error to inspect
   * @returns true when this is the empty-predictions error
   */
  public static isEmptyResponseError(error: any): boolean {
    if (error === null || error === undefined) {
      return false;
    }
    if (
      typeof error.getCode !== "function" ||
      typeof error.getMessage !== "function"
    ) {
      return false;
    }
    return (
      error.getCode() === HOMESCREEN_ERROR_CODE &&
      error.getMessage() === HOMESCREEN_EMPTY_RESPONSE_MESSAGE
    );
  }

  /**
   * Checks whether a successful API response has any displayable content.
   *
   * @param carousels - The carousels array from the screens API response
   * @returns true if the response is empty (triggers A707), false if content exists
   */
  public static isEmptyResponse(
    carousels: CarouselWithFeedItems[] | undefined | null
  ): boolean {
    if (!carousels || carousels.length === 0) {
      return true;
    }

    return carousels.every((carousel: CarouselWithFeedItems): boolean => {
      const feedItems = carousel.feedItems;
      if (feedItems === undefined || feedItems === null) {
        return true;
      }
      const items = feedItems.feedItems;
      return items === undefined || items === null || items.length === 0;
    });
  }

  /**
   * Composite code for the error dialog: `"A707-G500"`, or plain `"A707"`
   * when there is no sub-error code to show.
   *
   * @param error - The raw error thrown by the HTTP client or service layer
   * @returns The composite code to display and report
   */
  public static toCompositeCode(error: any): string {
    return this.fromError(error).getUIErrorwithSubErrorCode();
  }
}
